import { FC, useEffect, useCallback, useMemo, ReactNode, useState, createContext } from 'react'
import * as cn from 'classnames'
import { createPortal } from 'react-dom'
import styles from 'components/features/pfm/FloatingPaneHost/FloatingPaneHost.styl'
import { getMixedAccountsActionCreator, FetchMissingAccountsAction } from 'actions/linkageActions'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { createTopPaneSelector } from 'selectors/floatingPanesSelectors'
import { PreventScroll } from 'components/features/pfm/PreventScroll/PreventScroll'
import { killAllFloatingPanesActionCreator, killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { POPUP_PORTAL_ID } from 'constants/component'
import { TransactionRecatFloatingPane } from 'components/features/pfm/TransactionRecatFloatingPane/TransactionRecatFloatingPane'
import TransactionDetailsPane from 'components/features/transactions/TransactionDetailsPane/TransactionDetailsPane'
import { TrackerCreatorPane } from 'components/features/pfm/TrackerCreatorPane/TrackerCreatorPane'
import { OpportunityReviewFloatingPane } from 'components/features/pfm/OpportunityReviewFloatingPane/OpportunityReviewFloatingPane'
import { SubscriptionFloatingPane } from 'components/features/pfm/SubscriptionPane/SubscriptionPaneHost'
import { MixedAccountDetailsPane } from 'components/features/pfm/MixedAccountDetailsPane/MixedAccountDetailsPane'
import { ProviderSearchPane } from 'components/features/pfm/ProviderSearchPane/ProviderSearchPane'
import { HouseEditPane } from 'components/features/pfm/HouseEditPane/HouseEditPane'
import { CarEditPane } from 'components/features/pfm/CarEditPane/CarEditPane'
import { FutureFloatingPane } from 'components/features/future/FutureFloatingPane/FutureFloatingPane'
import { FastlinkPane } from 'components/features/fastlink/FastlinkPane/FastlinkPane'
import { AdvisorFloatingPaneHost } from 'components/features/coaching/advisorMeeting/AdvisorFloatingPaneHost/AdvisorFloatingPaneHost'
import { FLOATING_PANE_CRYPTO_REWARDS_TYPE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { FLOATING_PANE_INVITE_FRIENDS_PANE } from 'components/features/inviteFriendsPane/constants/pane'
import { InviteFriendsPane } from 'components/features/inviteFriendsPane/InviteFriendsPane'
import { CryptoRewardsPaneHost } from 'components/features/rewards/rewardsPane/containers/CryptoRewardsPaneHost/CryptoRewardsPaneHost'
import { PageFiltersPaneHost } from 'components/features/pageFilters/PageFiltersPaneHost/PageFiltersPaneHost'
import { PAGE_FILTER_PANE } from 'components/features/pageFilters/types'
import { CreditCardPaneHost } from 'components/features/creditCard/creditCardPanes/CreditCardPaneHost'
import { CREDIT_CARD_PANE } from 'components/features/creditCard/creditCardStore/floatingPane'
import { FloatingPane } from 'interfaces/floatingPanes'

type FloatingPanesHostProps = {}
type FloatingPaneContext = FloatingPane | null

export const PaneContext = createContext<FloatingPaneContext>(null)

export const FloatingPanesHost: FC<FloatingPanesHostProps> = (props) => {
  const {} = props

  const [hostNode, setHostNode] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (hostNode) {
      return
    }

    setHostNode(document.getElementById(POPUP_PORTAL_ID) as HTMLDivElement)
  }, [hostNode])

  const topPaneSelector = useMemo(createTopPaneSelector, [])
  const topPane = useStoreSelector(topPaneSelector)
  const dispatch = useStoreDispatch()

  const handleCoverClick = useCallback(() => {
    if (topPane?.type === 'fastlink') {
      dispatch(new FetchMissingAccountsAction({}))
      dispatch(getMixedAccountsActionCreator.request({}))
    } else if (topPane?.type === 'transaction-recat') {
      dispatch(killFloatingPanesActionCreator('transaction-recat'))
    }
    dispatch(killAllFloatingPanesActionCreator())
  }, [dispatch, topPane?.type])

  const preventScrollKey = useMemo(() => {
    if (!topPane) {
      return ''
    }

    if (topPane.type !== 'transaction-recat') {
      return topPane.type
    }

    return `${ topPane.type }:${ topPane.mode }`
  }, [topPane])
  const paneExists = !!topPane

  const paneChild = useMemo(() => {
    if (!topPane) {
      return null
    }

    let paneInnerChild: ReactNode | null = null

    switch (topPane.type) {
      case 'opportunity-review':
        paneInnerChild = (
          <OpportunityReviewFloatingPane />
        )
        break

      case 'transaction-details':
        paneInnerChild = (
          <TransactionDetailsPane uid={ topPane.uid } />
        )
        break

      case 'transaction-recat':
        paneInnerChild = (
          <TransactionRecatFloatingPane onClose={ handleCoverClick } />
        )
        break

      case 'tracker':
        paneInnerChild = (
          <TrackerCreatorPane />
        )
        break

      case 'subscription':
        paneInnerChild = (
          <SubscriptionFloatingPane />
        )
        break

      case 'mixed-account-details':
        paneInnerChild = (
          <MixedAccountDetailsPane />
        )
        break

      case 'fastlink':
        paneInnerChild = (
          <FastlinkPane { ...topPane } onCancel={ handleCoverClick } />
        )
        break

      case 'provider-search':
        paneInnerChild = (
          <ProviderSearchPane />
        )
        break

      case 'house-edit':
        paneInnerChild = (
          <HouseEditPane />
        )
        break

      case 'car-edit':
        paneInnerChild = (
          <CarEditPane />
        )
        break

      case 'future':
        paneInnerChild = (
          <FutureFloatingPane />
        )
        break

      case 'advisor':
        paneInnerChild = (
          <AdvisorFloatingPaneHost />
        )
        break

      case FLOATING_PANE_CRYPTO_REWARDS_TYPE:
        paneInnerChild = (
          <CryptoRewardsPaneHost />
        )
        break

      case FLOATING_PANE_INVITE_FRIENDS_PANE:
        paneInnerChild = (
          <InviteFriendsPane />
        )
        break

      case PAGE_FILTER_PANE:
        paneInnerChild = (
          <PageFiltersPaneHost pane={ topPane } />
        )
        break

      case CREDIT_CARD_PANE:
        paneInnerChild = (
          <CreditCardPaneHost pane={ topPane } />
        )
        break

      default:
        return null
    }

    return (
      <PreventScroll
        className={ styles.preventScrollWrapper }
        key={ preventScrollKey }
        allowScroll={ !paneExists }
      >
        { paneInnerChild }
      </PreventScroll>
    )
  }, [topPane, preventScrollKey, paneExists, handleCoverClick])


  const classNames = cn(styles.floatingPanesHost, { [styles.closed]: !paneExists })

  const paneWrapperChild = (
    <PaneContext.Provider value={ topPane }>
      <div className={ classNames }>
        <div className={ styles.cover } onClickCapture={ handleCoverClick } />
        <aside className={ styles.pane }>
          { paneChild }
        </aside>
      </div>
    </PaneContext.Provider>
  )

  if (!hostNode) {
    return null
  }

  return createPortal(paneWrapperChild, hostNode)
}
